/**
 * Reporte de conteos de inventario (backend: /api/conteos_reporte y
 * /api/conteos_reporte_detalle en conteosbackend.py).
 *
 * Una fila por conteo en el rango (fecha de apertura) con sucursal, alcance,
 * estado, duración, participantes y exactitud. Clic en la fila abre el
 * detalle por chasis: resultado, quién y cuándo lo escaneó y su situación
 * HOY en inventario (explica pendientes vendidos/transferidos y de qué
 * sucursal es cada sobrante), con resumen por participante y por modelo.
 *
 * Alcance: el selector de sucursal solo aparece con el checkbox "Ver
 * sucursal" del programa conteos (o admin / permiso versucursales). Sin él,
 * el backend fuerza la sucursal del usuario y aquí solo se avisa.
 */
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";
import { useGlobalMDAlert } from "context";
import { FP, FpDataGrid, StatusPill, fpInputSx } from "components/financepro";
import { fechaDMY, useApi, useSucursales } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";
import { alcanceLabel } from "modules/conteos/comunes";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const codigo = (label) =>
  String(label || "")
    .split("-")[0]
    .trim();
const stamp = () => new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const fmtDuracion = (m) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
};

const VARIANTE_ESTADO = { Cerrado: "success", Abierto: "warning", Descartado: "neutral" };
const VARIANTE_RESULTADO = { Contado: "success", Pendiente: "warning", Sobrante: "danger" };
const VARIANTE_ACTUAL = {
  "En inventario": "success",
  "En otra sucursal": "info",
  "En tránsito": "info",
  Incautado: "warning",
  "No registrado": "danger",
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = {
  ...btnBase,
  height: 46,
  width: "100%",
  bgcolor: FP.color.navy,
  color: "#FFFFFF",
  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
};
const btnOutline = {
  ...btnBase,
  color: FP.color.navy,
  bgcolor: "#FFFFFF",
  border: `1px solid ${FP.color.navy}`,
  "&:hover": {
    bgcolor: `${FP.color.navy}12`,
    border: `1px solid ${FP.color.navy}`,
    color: FP.color.navy,
  },
};

function Indicador({ label, value, color = FP.color.textPrimary, detalle }) {
  return (
    <Box
      sx={{
        flex: "1 1 130px",
        minWidth: 0,
        px: 1.8,
        py: 1.2,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        borderLeft: `4px solid ${color}`,
        bgcolor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.64rem",
          fontWeight: 800,
          color: FP.color.textSecondary,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "1.15rem", fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Typography>
      {detalle ? (
        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textSecondary, fontWeight: 600 }}>
          {detalle}
        </Typography>
      ) : null}
    </Box>
  );
}

function BotonesSalida({ onImprimir, onExcel, disabled }) {
  return (
    <Stack direction="row" spacing={1}>
      <Button
        startIcon={<PrintOutlinedIcon />}
        onClick={onImprimir}
        disabled={disabled}
        sx={btnOutline}
      >
        Imprimir
      </Button>
      <Button
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={onExcel}
        disabled={disabled}
        sx={btnOutline}
      >
        Excel
      </Button>
    </Stack>
  );
}

function descargarExcel(hojas, nombre) {
  const libro = XLSX.utils.book_new();
  hojas.forEach(({ titulo, aoa, anchos }) => {
    const hoja = XLSX.utils.aoa_to_sheet(aoa);
    if (anchos) hoja["!cols"] = anchos.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(libro, hoja, titulo);
  });
  XLSX.writeFile(libro, `${nombre}_${stamp()}.xlsx`);
}

const pill = (label, variantes) => (
  <Stack direction="row" alignItems="center" sx={{ height: "100%" }}>
    <StatusPill label={label} variant={variantes[label] || "neutral"} />
  </Stack>
);

// ---------------------------------------------------------------------------
// Listado de conteos
// ---------------------------------------------------------------------------

const COLS = [
  {
    field: "conteo_id",
    h: "#",
    w: 70,
    get: (r) => (r.origen === "legacy" ? `V${r.conteo_id}` : r.conteo_id),
  },
  { field: "abierto_en", h: "Apertura", w: 140, get: (r) => r.abierto_en },
  { field: "sucursal", h: "Sucursal", flex: 1, minWidth: 130, get: (r) => r.sucursal },
  { field: "alcance", h: "Alcance", flex: 1, minWidth: 150, get: (r) => alcanceLabel(r) },
  { field: "estadolabel", h: "Estado", w: 115, get: (r) => r.estadolabel, pill: VARIANTE_ESTADO },
  { field: "abierto_por", h: "Abrió", w: 105, get: (r) => r.abierto_por },
  { field: "cerrado_por", h: "Cerró", w: 105, get: (r) => (r.estado === "A" ? "" : r.cerrado_por) },
  { field: "minutos", h: "Duración", w: 100, get: (r) => fmtDuracion(r.minutos) },
  { field: "participantes", h: "Partic.", w: 75, right: true, get: (r) => r.participantes },
  { field: "totalesperado", h: "Esp.", w: 70, right: true, get: (r) => r.totalesperado },
  { field: "totalcontado", h: "Cont.", w: 70, right: true, get: (r) => r.totalcontado },
  { field: "totalpendiente", h: "Pend.", w: 70, right: true, get: (r) => r.totalpendiente },
  { field: "totalsobrante", h: "Sobr.", w: 70, right: true, get: (r) => r.totalsobrante },
  { field: "exactitud", h: "Exactitud", w: 95, right: true, get: (r) => fmtPct(r.exactitud) },
];

export default function ReporteConteos() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const navigate = useNavigate();
  const sucursales = useSucursales({ todas: true });
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [sucursal, setSucursal] = useState("");
  const [estado, setEstado] = useState("C");
  const [alcance, setAlcance] = useState("");
  const [participante, setParticipante] = useState("");
  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [forzada, setForzada] = useState("");
  const [versucursales, setVersucursales] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/conteos_reporte", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal,
        estado,
        alcance,
        participante: participante.trim(),
      });
      if (ok) {
        setFilas(data.filas || []);
        setTotales(data.totales || null);
        setForzada(data.sucursalforzada || "");
        setVersucursales(!!data.versucursales);
      } else {
        setFilas([]);
        setTotales(null);
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nombreSuc = (c) => sucursales.find((s) => codigo(s.label) === c)?.label || c;
  const subtitulo = [
    `Conteos abiertos del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`,
    forzada
      ? `Sucursal ${forzada}`
      : sucursal
      ? `Sucursal ${nombreSuc(sucursal)}`
      : "Todas las sucursales",
    estado ? { C: "Cerrados", A: "Abiertos", X: "Descartados" }[estado] : "Todos los estados",
    alcance ? { T: "Toda la sucursal", M: "Por marca", D: "Por modelo" }[alcance] : "",
    participante.trim() ? `Participante "${participante.trim()}"` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const t = totales || {};
  const filaTotal = () =>
    COLS.map((c) => {
      if (c.field === "conteo_id") return "TOTAL";
      if (c.field === "totalesperado") return t.esperado ?? 0;
      if (c.field === "totalcontado") return t.contado ?? 0;
      if (c.field === "totalpendiente") return t.pendiente ?? 0;
      if (c.field === "totalsobrante") return t.sobrante ?? 0;
      if (c.field === "exactitud") return fmtPct(t.exactitud);
      return "";
    });

  const imprimir = () =>
    imprimirListadoGrande({
      titulo: "Conteos de inventario",
      subtitulo,
      cabeceras: COLS.map((c) => c.h),
      filas: filas.map((r) => COLS.map((c) => c.get(r))),
      totales: filaTotal(),
      numericas: COLS.map((c, i) => (c.right ? i : -1)).filter((i) => i >= 0),
    });

  const exportar = () =>
    descargarExcel(
      [
        {
          titulo: "Conteos",
          aoa: [
            ["Conteos de inventario"],
            [subtitulo],
            [],
            [...COLS.map((c) => c.h), "Cierre", "Participantes", "Escaneos"],
            ...filas.map((r) => [
              ...COLS.map((c) => c.get(r)),
              r.estado === "A" ? "" : r.cerrado_en,
              r.usuarios,
              r.escaneos ?? "",
            ]),
            [],
            filaTotal(),
          ],
          anchos: [8, 18, 18, 24, 12, 12, 12, 12, 8, 8, 8, 8, 8, 10, 18, 30, 9],
        },
      ],
      "Conteos"
    );

  const columns = COLS.map((c) => ({
    field: c.field,
    headerName: c.h,
    ...(c.flex ? { flex: c.flex, minWidth: c.minWidth } : { width: c.w }),
    ...(c.right ? { align: "right", headerAlign: "right" } : {}),
    valueGetter: (_v, row) =>
      ["minutos", "exactitud"].includes(c.field) ? row[c.field] : c.get(row),
    ...(["minutos", "exactitud"].includes(c.field) ? { renderCell: (p) => c.get(p.row) } : {}),
    ...(c.pill ? { renderCell: (p) => pill(c.get(p.row), c.pill) } : {}),
  }));

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <Box
        sx={{
          px: { xs: 1.5, md: 3 },
          py: { xs: 1.75, md: 2 },
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate("/inventario/conteos")}
          sx={{
            color: FP.color.textSecondary,
            border: `1px solid ${FP.color.border}`,
            borderRadius: 1.4,
            width: 36,
            height: 36,
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box minWidth={0}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", md: "1.25rem" } }}>
            Reporte de conteos
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
            Exactitud, participantes y situación actual de cada chasis contado
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={4} md={1.6}>
            <TextField
              fullWidth
              type="date"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={fecha1}
              onChange={(e) => setFecha1(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.6}>
            <TextField
              fullWidth
              type="date"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={fecha2}
              onChange={(e) => setFecha2(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          {versucursales ? (
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={codigo(s.label)}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}
          <Grid item xs={6} sm={4} md={1.6}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              sx={fpInputSx}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="C">Cerrados</MenuItem>
              <MenuItem value="A">Abiertos</MenuItem>
              <MenuItem value="X">Descartados</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={1.8}>
            <TextField
              select
              fullWidth
              label="Alcance"
              value={alcance}
              onChange={(e) => setAlcance(e.target.value)}
              sx={fpInputSx}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="T">Toda la sucursal</MenuItem>
              <MenuItem value="M">Por marca</MenuItem>
              <MenuItem value="D">Por modelo</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={8} sm={4} md={1.8}>
            <TextField
              fullWidth
              label="Participante"
              value={participante}
              onChange={(e) => setParticipante(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && cargar()}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={4} sm={4} md={1}>
            <Button onClick={cargar} disabled={cargando} sx={btnSolid} aria-label="Procesar">
              {cargando ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            </Button>
          </Grid>
        </Grid>

        {forzada ? (
          <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mb: 1 }}>
            Solo se muestran los conteos de su sucursal ({forzada}). Para consultar otras, su rol
            necesita &quot;Ver sucursal&quot; en Conteos de Inventario.
          </Typography>
        ) : null}

        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.2} sx={{ mb: 1.5 }}>
          <Indicador
            label="Conteos"
            value={t.conteos ?? 0}
            color={FP.color.navy}
            detalle={`${t.cerrados ?? 0} cerrados · ${t.abiertos ?? 0} abiertos`}
          />
          <Indicador label="Esperado" value={t.esperado ?? 0} color={FP.color.navy} />
          <Indicador label="Contado" value={t.contado ?? 0} color={FP.color.successDark} />
          <Indicador label="Pendiente" value={t.pendiente ?? 0} color={FP.color.warning} />
          <Indicador label="Sobrante" value={t.sobrante ?? 0} color={FP.color.danger} />
          <Indicador
            label="Exactitud"
            value={fmtPct(t.exactitud)}
            color={FP.color.info}
            detalle="contado / esperado"
          />
        </Stack>

        <Stack direction="row" alignItems="center" sx={{ mb: 1 }} spacing={1}>
          <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, flex: 1 }}>
            {subtitulo}. Haga clic en un conteo para ver el detalle por chasis.
          </Typography>
          <BotonesSalida onImprimir={imprimir} onExcel={exportar} disabled={!filas.length} />
        </Stack>

        <Box sx={{ bgcolor: "#fff", overflowX: "auto" }}>
          <FpDataGrid
            autoHeight
            rows={filas}
            columns={columns}
            loading={cargando}
            onRowClick={(p) => setDetalle(p.row)}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            vacioTitulo="Sin conteos en el rango"
            vacioSubtitulo='Ajuste los filtros y haga clic en "Procesar".'
            sx={{ minWidth: 1200, "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Box>

      <DetalleConteo fila={detalle} onClose={() => setDetalle(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Detalle por chasis
// ---------------------------------------------------------------------------

const COLS_LINEA = [
  { field: "resultado", h: "Resultado", w: 110, get: (r) => r.resultado, pill: VARIANTE_RESULTADO },
  { field: "chasis", h: "Chasis", w: 190, get: (r) => r.chasis },
  { field: "marca", h: "Marca", w: 110, get: (r) => r.marca },
  { field: "modelo", h: "Modelo", w: 120, get: (r) => r.modelo },
  { field: "color", h: "Color", w: 95, get: (r) => r.color },
  { field: "tipo", h: "Tipo", w: 85, get: (r) => r.tipo },
  { field: "usuario", h: "Escaneó", w: 105, get: (r) => r.usuario },
  { field: "fecha", h: "Fecha escaneo", w: 140, get: (r) => r.fecha },
  { field: "actual", h: "Situación actual", w: 150, get: (r) => r.actual, pill: VARIANTE_ACTUAL },
  {
    field: "sucursalactual",
    h: "Sucursal actual",
    flex: 1,
    minWidth: 120,
    get: (r) => r.sucursalactual,
  },
];

const COLS_USUARIO = [
  { field: "usuario", h: "Participante", flex: 1, minWidth: 140, get: (r) => r.usuario },
  { field: "escaneos", h: "Escaneos", w: 95, right: true, get: (r) => r.escaneos },
  { field: "contados", h: "Contados", w: 95, right: true, get: (r) => r.contados },
  { field: "sobrantes", h: "Sobrantes", w: 95, right: true, get: (r) => r.sobrantes },
  { field: "primero", h: "Primer escaneo", w: 150, get: (r) => r.primero },
  { field: "ultimo", h: "Último escaneo", w: 150, get: (r) => r.ultimo },
];

const COLS_MODELO = [
  { field: "marca", h: "Marca", w: 130, get: (r) => r.marca },
  { field: "modelo", h: "Modelo", flex: 1, minWidth: 140, get: (r) => r.modelo },
  { field: "esperado", h: "Esperado", w: 95, right: true, get: (r) => r.esperado },
  { field: "contado", h: "Contado", w: 95, right: true, get: (r) => r.contado },
  { field: "pendiente", h: "Pendiente", w: 95, right: true, get: (r) => r.pendiente },
  { field: "exactitud", h: "Exactitud", w: 95, right: true, get: (r) => fmtPct(r.exactitud) },
];

const aColumnas = (cols) =>
  cols.map((c) => ({
    field: c.field,
    headerName: c.h,
    ...(c.flex ? { flex: c.flex, minWidth: c.minWidth } : { width: c.w }),
    ...(c.right ? { align: "right", headerAlign: "right" } : {}),
    valueGetter: (_v, row) => (c.field === "exactitud" ? row.exactitud : c.get(row)),
    ...(c.field === "exactitud" ? { renderCell: (p) => c.get(p.row) } : {}),
    ...(c.pill ? { renderCell: (p) => pill(c.get(p.row), c.pill) } : {}),
  }));

function DetalleConteo({ fila, onClose }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tab, setTab] = useState(0);
  const [resultado, setResultado] = useState("");
  const [actual, setActual] = useState("");

  useEffect(() => {
    if (!fila) {
      setData(null);
      return;
    }
    setTab(0);
    setResultado("");
    setActual("");
    setCargando(true);
    post("/api/conteos_reporte_detalle", { conteo_id: fila.conteo_id, origen: fila.origen })
      .then(({ ok, data: d }) => {
        if (ok) setData(d);
        else {
          showAlert({ message: d.Error || "No se pudo cargar el detalle", color: "error" });
          onClose();
        }
      })
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fila]);

  const lineas = data?.lineas || [];
  const situaciones = useMemo(() => [...new Set(lineas.map((l) => l.actual))].sort(), [lineas]);
  const visibles = lineas.filter(
    (l) => (!resultado || l.resultado === resultado) && (!actual || l.actual === actual)
  );

  if (!fila) return null;
  const titulo = `Conteo ${fila.origen === "legacy" ? "V" : "#"}${fila.conteo_id}`;
  const subtitulo = data
    ? [data.sucursal, alcanceLabel(data), data.estadolabel, data.abierto_en]
        .filter(Boolean)
        .join(" · ")
    : "";

  const imprimir = () =>
    imprimirListadoGrande({
      titulo: `${titulo} — detalle por chasis`,
      subtitulo: [
        subtitulo,
        resultado ? `Resultado ${resultado}` : "",
        actual ? `Situación ${actual}` : "",
        `Exactitud ${fmtPct(data?.exactitud)}`,
      ]
        .filter(Boolean)
        .join(" · "),
      cabeceras: COLS_LINEA.map((c) => c.h),
      filas: visibles.map((r) => COLS_LINEA.map((c) => c.get(r))),
    });

  const exportar = () => {
    const hoja = (nombre, cols, rows) => ({
      titulo: nombre,
      aoa: [
        [titulo],
        [subtitulo],
        [],
        cols.map((c) => c.h),
        ...rows.map((r) => cols.map((c) => c.get(r))),
      ],
      anchos: cols.map((c) => Math.max(10, Math.round((c.w || c.minWidth || 120) / 7))),
    });
    descargarExcel(
      [
        hoja("Chasis", [...COLS_LINEA, { h: "Dispositivo", get: (r) => r.dispositivo }], lineas),
        hoja("Participantes", COLS_USUARIO, data?.porusuario || []),
        hoja("Por modelo", COLS_MODELO, data?.pormodelo || []),
      ],
      `Conteo_${fila.conteo_id}`
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", flex: 1 }}>{titulo}</Typography>
          <BotonesSalida onImprimir={imprimir} onExcel={exportar} disabled={!data} />
        </Stack>
        <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, fontWeight: 600 }}>
          {subtitulo}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {cargando || !data ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: FP.color.navy }} />
          </Box>
        ) : (
          <>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.2} sx={{ mb: 1.5 }}>
              <Indicador label="Esperado" value={data.totalesperado} color={FP.color.navy} />
              <Indicador label="Contado" value={data.totalcontado} color={FP.color.successDark} />
              <Indicador label="Pendiente" value={data.totalpendiente} color={FP.color.warning} />
              <Indicador label="Sobrante" value={data.totalsobrante} color={FP.color.danger} />
              <Indicador label="Exactitud" value={fmtPct(data.exactitud)} color={FP.color.info} />
            </Stack>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5 }}>
              <Tab label={`Chasis (${lineas.length})`} sx={{ textTransform: "none" }} />
              <Tab
                label={`Participantes (${(data.porusuario || []).length})`}
                sx={{ textTransform: "none" }}
              />
              <Tab label="Por modelo" sx={{ textTransform: "none" }} />
            </Tabs>
            {tab === 0 ? (
              <>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={6} md={2.5}>
                    <TextField
                      select
                      fullWidth
                      label="Resultado"
                      value={resultado}
                      onChange={(e) => setResultado(e.target.value)}
                      sx={fpInputSx}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="Contado">Contados</MenuItem>
                      <MenuItem value="Pendiente">Pendientes</MenuItem>
                      <MenuItem value="Sobrante">Sobrantes</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={2.5}>
                    <TextField
                      select
                      fullWidth
                      label="Situación actual"
                      value={actual}
                      onChange={(e) => setActual(e.target.value)}
                      sx={fpInputSx}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {situaciones.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Box sx={{ overflowX: "auto" }}>
                  <FpDataGrid
                    autoHeight
                    rows={visibles}
                    columns={aColumnas(COLS_LINEA)}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    sx={{ minWidth: 1250 }}
                  />
                </Box>
              </>
            ) : tab === 1 ? (
              <FpDataGrid
                autoHeight
                rows={data.porusuario || []}
                columns={aColumnas(COLS_USUARIO)}
                getRowId={(r) => r.usuario}
                hideFooter
              />
            ) : (
              <FpDataGrid
                autoHeight
                rows={data.pormodelo || []}
                columns={aColumnas(COLS_MODELO)}
                getRowId={(r) => `${r.marca}|${r.modelo}`}
                hideFooter={(data.pormodelo || []).length <= 25}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={btnOutline}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
